import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from '@/supabaseClient';
import { toLogicDate } from '@/utils/date-utils';

export interface SupplementCatalogItem {
    id: string;
    brand: string;
    product_name: string;
    measure_unit: string;
    unit_weight_g: number;
    active_ingredients: any;
}

export interface ProtocolItem {
    id: string;
    catalog_id: string;
    dosage_count: number;
    timing_window: 'MORNING' | 'LUNCH' | 'PRE_WO' | 'POST_WO' | 'SLEEP';
    conditional_logic: any;
    catalog_item?: SupplementCatalogItem;
}

export interface SupplementProtocol {
    id: string;
    name: string;
    version: string;
    items: ProtocolItem[];
}

export interface SupplementGhost {
    window: string;
    scheduledTime: Date;
    label: string;
    isCrystallized: boolean;
    items: ProtocolItem[];
    warnings: string[];
}

interface SupplementState {
    activeProtocol: SupplementProtocol | null;
    isLoading: boolean;
    error: string | null;
    dismissedSuggestions: string[]; // Format: "YYYY-MM-DD:WINDOW"
    fetchActiveProtocol: () => Promise<void>;
    getDailyProjections: (date: Date, allEvents: any[], dailySnapshot: any) => SupplementGhost[];
    dismissSuggestion: (dateStr: string, window: string) => void;
}

export const useSupplementStore = create<SupplementState>()(
    persist(
        (set, get) => ({
            activeProtocol: null,
            isLoading: false,
            error: null,
            dismissedSuggestions: [],

            fetchActiveProtocol: async () => {
                set({ isLoading: true });
                try {
                    const { data: proto, error: protoError } = await supabase
                        .schema('v3')
                        .from('supplement_protocols')
                        .select('*, items:supplement_protocol_items(*, catalog_item:supplement_catalog(*))')
                        .eq('is_active', true)
                        .maybeSingle();

                    if (protoError) throw protoError;
                    set({ activeProtocol: proto as any, isLoading: false });
                } catch (err: any) {
                    console.error("[SupplementStore] Fetch failed:", err);
                    set({ error: err.message, isLoading: false });
                }
            },

            dismissSuggestion: (dateStr, window) => {
                const id = `${dateStr}:${window}`;
                if (!get().dismissedSuggestions.includes(id)) {
                    set(state => ({ dismissedSuggestions: [...state.dismissedSuggestions, id] }));
                }
            },

            getDailyProjections: (date, allEvents, dailySnapshot) => {
                const protocol = get().activeProtocol;
                if (!protocol) return [];

                const dateStr = toLogicDate(date);
                const dayEvents = (allEvents || []).filter(e => 
                    toLogicDate(e.start_time) === dateStr
                );

                const windows = ['MORNING', 'LUNCH', 'PRE_WO', 'POST_WO', 'SLEEP'];
                const projections: SupplementGhost[] = [];

                // 1. Find anchor events
                const performanceEvents = dayEvents.filter(e => e.type === 'PERFORMANCE' && e.status !== 'SKIPPED');
                const nutritionEvents = dayEvents.filter(e => (e.type === 'NUTRITION' || e.type === 'nutrition') && e.status !== 'SKIPPED');
                const completedSupps = dayEvents.filter(e => e.type === 'SUPPLEMENTATION' || e.type === 'SUPPLEMENT');

                windows.forEach(window => {
                    const windowItems = protocol.items.filter(it => it.timing_window === window);
                    if (windowItems.length === 0) return;

                    // Calculate Projected Time
                    let scheduledTime = new Date(date);
                    if (window === 'MORNING') scheduledTime.setHours(8, 0, 0, 0);
                    else if (window === 'LUNCH') {
                        if (nutritionEvents.length > 0) {
                            scheduledTime = new Date(nutritionEvents[0].start_time);
                        } else {
                            scheduledTime.setHours(13, 0, 0, 0);
                        }
                    } else if (window === 'PRE_WO') {
                        if (performanceEvents.length > 0) {
                            scheduledTime = new Date(new Date(performanceEvents[0].start_time).getTime() - 30 * 60000);
                        } else {
                            return; // No workout, no pre-workout ghost
                        }
                    } else if (window === 'POST_WO') {
                        if (performanceEvents.length > 0) {
                            const lastWorkout = performanceEvents[performanceEvents.length - 1];
                            scheduledTime = new Date(new Date(lastWorkout.end_time || lastWorkout.start_time).getTime());
                        } else {
                            return; // No workout, no post-workout ghost
                        }
                    } else if (window === 'SLEEP') {
                        scheduledTime.setHours(22, 0, 0, 0);
                    }

                    // Check if already crystallized (logged)
                    const isCrystallized = completedSupps.some(e => e.payload?.timing_window === window || e.payload?.window === window);

                    // 2. INTELLIGENCE: Check against Nutrition
                    const warnings: string[] = [];
                    windowItems.forEach(it => {
                        const logic = it.conditional_logic || {};
                        
                        // Check for specific exclusions (e.g. fatty fish -> skip omega 3)
                        if (logic.exclude_if_logged && dailySnapshot) {
                            const nutrition = dailySnapshot.nutrition || {};
                            if (logic.exclude_if_logged.includes('fatty_fish')) {
                                // If EPA/DHA is already high from food, suggest skip
                                if ((nutrition.micros?.omega_3?.total || 0) > 1000) {
                                    warnings.push(`Skip ${it.catalog_item?.product_name}: Daily Omega-3 target already met via whole foods.`);
                                }
                            }
                        }

                        // Check for day skips
                        const dayName = date.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase();
                        if (logic.skip_days && logic.skip_days.includes(dayName)) {
                            warnings.push(`Protocol: Skip ${it.catalog_item?.product_name} today (${dayName}).`);
                        }
                    });

                    projections.push({
                        window,
                        scheduledTime,
                        label: `${window.replace('_', ' ')} STACK`,
                        isCrystallized,
                        items: windowItems,
                        warnings
                    });
                });

                return projections;
            }
        }),
        { 
            name: 'mp-supplement-store-v2',
            partialize: (state) => ({ dismissedSuggestions: state.dismissedSuggestions })
        }
    )
);
